import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { CopyButton } from '../components/CopyButton';
import { useCrumb } from '../lib/crumb';
import { DetailHead } from '../components/DetailHead';
import { DetailBody, RailCard } from '../components/DetailRail';
import { Ownership, type Owner } from '../components/Ownership';
import { Icon } from '../components/Icon';

const CLUSTER = gql`
  query Cluster($name: String!) {
    cluster(name: $name) {
      id
      name
      owners {
        ownerId
        role
        displayName
        email
      }
      clusterId
      cloudProvider
      estimatedMonthlyCost
      doAccount
      region
      k8sVersion
      lbIp
      nodeCount
      ingressType
      certMode
      alloyOnboarded
      reachability
      namespaceCount
      confidence
      firstSeenAt
      lastSeenAt
      description
      nodeInfo
      health {
        monitored
        scrapeUpRatio
        nodesReady
        nodesTotal
        podsCrashlooping
        cpuUsedPct
        memUsedPct
        certExpiryDays
      }
      namespaces {
        id
        name
      }
    }
  }
`;

interface NodeDetail {
  name: string;
  pool: string | null;
  instanceType: string | null;
  region: string | null;
  kubeletVersion: string | null;
  ready: boolean;
  cpu: string | null;
  memory: string | null;
}
interface Cluster {
  id: string;
  name: string;
  owners: Owner[];
  clusterId?: string;
  cloudProvider?: string;
  estimatedMonthlyCost?: number;
  doAccount?: string;
  region?: string;
  k8sVersion?: string;
  lbIp?: string;
  nodeCount?: number;
  ingressType?: string;
  certMode?: string;
  alloyOnboarded?: boolean;
  reachability?: string;
  namespaceCount: number;
  confidence: string;
  firstSeenAt: string;
  lastSeenAt: string;
  description?: string;
  nodeInfo?: string;
  health?: {
    monitored: boolean;
    scrapeUpRatio?: number | null;
    nodesReady?: number | null;
    nodesTotal?: number | null;
    podsCrashlooping?: number | null;
    cpuUsedPct?: number | null;
    memUsedPct?: number | null;
    certExpiryDays?: number | null;
  };
  namespaces: { id: string; name: string }[];
}
interface Data {
  cluster: Cluster | null;
}

type Tab = 'overview' | 'resources' | 'extensions';
const TABS: { key: Tab; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'resources', label: 'Resources' },
  { key: 'extensions', label: 'Extensions' },
];

export function ClusterDetailPage() {
  const { name = '' } = useParams();
  const [tab, setTab] = useState<Tab>('overview');
  const { data, loading, error, refetch } = useQuery<Data>(CLUSTER, {
    variables: { name },
    fetchPolicy: 'cache-and-network',
  });

  const c = data?.cluster;
  const pools = useMemo(() => groupPools(c?.nodeInfo), [c?.nodeInfo]);

  // Breadcrumb tail — the topbar knows the route, not the entity.
  // MUST sit above the early returns: called after them, it would be a
  // conditional hook and React would throw once the data arrived.
  useCrumb(c?.name);

  if (loading && !data) return <div className="page">Loading…</div>;
  if (error) return <div className="page muted">Failed to load: {error.message}</div>;
  if (!c)
    return (
      <div className="page">
        <p className="muted">Cluster “{name}” not found.</p>
        <Link to="/clusters">← Back to clusters</Link>
      </div>
    );

  return (
    <div className="page">
      <div className="detail-back">
        <Link to="/clusters" className="muted">
          ← Clusters
        </Link>
      </div>
      <DetailHead
        name={c.name}
        badge={
          <span className={`badge ${c.reachability === 'reachable' ? 'confirmed' : 'inferred'}`}>
            {c.reachability === 'reachable' ? 'reachable' : 'unreachable'}
          </span>
        }
        facts={[
          c.region,
          c.k8sVersion && `k8s ${c.k8sVersion}`,
          c.nodeCount != null && `${c.nodeCount} node${c.nodeCount === 1 ? '' : 's'}`,
          `${c.namespaceCount} namespace${c.namespaceCount === 1 ? '' : 's'}`,
        ]}
        scannedAt={c.lastSeenAt}
        actions={<CopyButton value={c.name} />}
      />

      <DetailBody
        rail={
          <>
            {/* No RailCard wrapper: Ownership renders its own heading. */}
            <div className="rail-card">
              <Ownership
                subjectType="cluster"
                subjectId={c.id}
                owners={c.owners}
                onChanged={() => void refetch()}
              />
            </div>
            <RailCard title="Access">
              <div className="stack-sm">
                {c.lbIp && (
                  <div className="ip-cell">
                    {/* An IP is an identifier, not a code snippet — phase 6 §1 mono policy. */}
                    <span>{c.lbIp}</span>
                    <CopyButton value={c.lbIp} />
                  </div>
                )}
                <span className="muted text-2xs">{c.doAccount ?? 'account unknown'}</span>
                <Link
                  to={`/access/new?type=cluster&entityId=${c.id}&name=${encodeURIComponent(c.name)}`}
                >
                  Request access
                </Link>
              </div>
            </RailCard>
            <RailCard title="Elsewhere">
              <div className="stack-sm">
                <Link to={`/repos?view=graph&cluster=${encodeURIComponent(c.name)}`}>
                  What runs here (graph)
                </Link>
                <Link to="/atlas-whitelist">Atlas egress IPs</Link>
              </div>
            </RailCard>
          </>
        }
      >
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t.key} className={tab === t.key ? 'active' : ''} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <Overview c={c} />}
      {tab === 'resources' && <Resources pools={pools} />}
      {tab === 'extensions' && <Extensions c={c} />}
      </DetailBody>
    </div>
  );
}

function Overview({ c }: { c: Cluster }) {
  const facts: [string, React.ReactNode][] = [
    ['Estimated cost', c.estimatedMonthlyCost != null ? `~$${c.estimatedMonthlyCost} / mo` : '—'],
    ['Cluster ID', c.clusterId ? <span className="ip-cell">{c.clusterId}<CopyButton value={c.clusterId} /></span> : '—'],
    ['Cloud Provider', c.cloudProvider ?? '—'],
    ['DO account', c.doAccount ?? '—'],
    ['Region', c.region ?? '—'],
    ['Version', c.k8sVersion ? <span className="id-compact">{c.k8sVersion}</span> : '—'],
    ['LB IP', c.lbIp ? <span className="ip-cell">{c.lbIp}<CopyButton value={c.lbIp} /></span> : '—'],
    ['Nodes', c.nodeCount ?? '—'],
    ['Namespaces', c.namespaceCount],
    ['Last scanned', new Date(c.lastSeenAt).toLocaleString()],
  ];
  return (
    <>
      {c.description && <p className="detail-desc">{c.description}</p>}
      <div className="kv-grid">
        {facts.map(([k, v]) => (
          <div className="kv" key={k}>
            <div className="kv-k">{k}</div>
            <div className="kv-v">{v}</div>
          </div>
        ))}
      </div>
      {c.estimatedMonthlyCost != null && (
        <p className="muted text-2xs" style={{ marginTop: 8 }}>
          Cost is an estimate from node instance-type list prices, not actual DO billing.
        </p>
      )}

      <h3 className="section">Live health</h3>
      {c.health?.monitored ? (
        <div className="kv-grid">
          {(
            [
              ['Scrape up', c.health.scrapeUpRatio != null ? `${Math.round(c.health.scrapeUpRatio * 100)}%` : '—'],
              ['Nodes ready', c.health.nodesTotal != null ? `${c.health.nodesReady ?? 0}/${c.health.nodesTotal}` : '—'],
              ['Crashlooping pods', c.health.podsCrashlooping ?? 0],
              ['CPU used', c.health.cpuUsedPct != null ? `${c.health.cpuUsedPct}%` : '—'],
              ['Mem used', c.health.memUsedPct != null ? `${c.health.memUsedPct}%` : '—'],
              ['Cert expiry', c.health.certExpiryDays != null ? `${Math.round(c.health.certExpiryDays)} days` : '—'],
            ] as [string, React.ReactNode][]
          ).map(([k, v]) => (
            <div className="kv" key={k}>
              <div className="kv-k">{k}</div>
              <div className="kv-v">{v}</div>
            </div>
          ))}
        </div>
      ) : (
        <p className="muted">
          Not monitored — no Prometheus scrape onboarded for this cluster (or Grafana not
          configured). Live health appears once Alloy is shipping metrics.
        </p>
      )}

      <h3 className="section">Namespaces ({c.namespaces.length})</h3>
      <div className="ns-grid">
        {c.namespaces.map((n) => (
          <span key={n.id} className="badge id-compact">
            {n.name}
          </span>
        ))}
      </div>
    </>
  );
}

function Resources({ pools }: { pools: Pool[] }) {
  if (pools.length === 0)
    return <p className="muted">No node data (cluster may be unreachable).</p>;
  return (
    <>
      <h3 className="section">Node pools ({pools.length})</h3>
      {pools.map((p) => (
        <div key={p.name} className="pool">
          <div className="pool-head">
            <strong>{p.name}</strong>
            <span className="badge">{p.instanceType}</span>
            <span className="muted">
              {p.nodes.length} node{p.nodes.length === 1 ? '' : 's'}
            </span>
          </div>
          <table>
            <thead>
              <tr>
                <th>VM (node)</th>
                <th>Size</th>
                <th>CPU</th>
                <th>Memory</th>
                <th>Kubelet</th>
                <th>Ready</th>
              </tr>
            </thead>
            <tbody>
              {p.nodes.map((n) => (
                <tr key={n.name}>
                  <td>{n.name}</td>
                  <td className="muted">{n.instanceType ?? '—'}</td>
                  <td className="muted">{n.cpu ?? '—'}</td>
                  <td className="muted">{fmtMem(n.memory)}</td>
                  <td className="muted">{n.kubeletVersion ?? '—'}</td>
                  <td>{n.ready ? <Icon name="check" /> : <Icon name="cross" />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </>
  );
}

interface Ext {
  name: string;
  detail: string;
}
function Extensions({ c }: { c: Cluster }) {
  const ns = new Set(c.namespaces.map((n) => n.name));
  const exts: Ext[] = [];
  if (c.ingressType === 'ingress-nginx' || ns.has('ingress-nginx'))
    exts.push({ name: 'Nginx Ingress Controller', detail: `Ingress controller${c.lbIp ? ` · LB ${c.lbIp}` : ''}` });
  if (c.ingressType === 'traefik')
    exts.push({ name: 'Traefik', detail: `Ingress/proxy${c.lbIp ? ` · LB ${c.lbIp}` : ''}` });
  if (c.certMode === 'cert-manager' || ns.has('cert-manager'))
    exts.push({ name: 'Cert-Manager', detail: 'Automated TLS certificate issuance (Let’s Encrypt)' });
  if (c.alloyOnboarded || ns.has('alloy'))
    exts.push({ name: 'Grafana Alloy', detail: 'Observability agent — ships logs/metrics to the stack' });
  if (ns.has('monitoring'))
    exts.push({ name: 'Monitoring (Prometheus)', detail: 'In-cluster metrics stack (ns monitoring)' });
  if (ns.has('kubernetes-dashboard'))
    exts.push({ name: 'Kubernetes Dashboard', detail: 'Web UI for the cluster (ns kubernetes-dashboard)' });

  if (exts.length === 0)
    return <p className="muted">No known extensions detected (from ingress type, cert-manager, alloy, or add-on namespaces).</p>;

  return (
    <>
      <h3 className="section">Extensions ({exts.length})</h3>
      <div className="ext-grid">
        {exts.map((e) => (
          <div className="ext-card" key={e.name}>
            <div className="ext-name">
              <span className="dot" /> {e.name}
            </div>
            <div className="muted text-xs" style={{ marginTop: 4 }}>
              {e.detail}
            </div>
          </div>
        ))}
      </div>
      <p className="muted text-2xs" style={{ marginTop: 10 }}>
        Detected from ingress type + add-on namespaces. Deeper add-on inventory (Helm releases,
        operators) is a follow-up.
      </p>
    </>
  );
}

interface Pool {
  name: string;
  instanceType: string | null;
  nodes: NodeDetail[];
}
function groupPools(nodeInfoJson?: string): Pool[] {
  if (!nodeInfoJson) return [];
  let nodes: NodeDetail[] = [];
  try {
    nodes = JSON.parse(nodeInfoJson).nodes ?? [];
  } catch {
    return [];
  }
  const byPool = new Map<string, Pool>();
  for (const n of nodes) {
    const key = n.pool || '(no pool)';
    if (!byPool.has(key)) byPool.set(key, { name: key, instanceType: n.instanceType, nodes: [] });
    byPool.get(key)!.nodes.push(n);
  }
  return [...byPool.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/** "32872676Ki" → "31 GB". */
function fmtMem(mem: string | null): string {
  if (!mem) return '—';
  const m = mem.match(/^(\d+)Ki$/);
  if (m) return `${Math.round(Number(m[1]) / 1024 / 1024)} GB`;
  return mem;
}
